/** Passive measurements from already loaded logs. Unknown timestamps stay unknown. */
export interface TimingLog {
  timestamp?: Date | string | number | null;
  type?: string | null;
  payload?: string | null;
}
export function measureCallLatency(logs: TimingLog[]) {
  const result = {
    sttAfterSpeechMs: null as number | null,
    llmFirstTokenMs: null as number | null,
    ttsFirstChunkMs: null as number | null,
    speechToFirstAudioMs: null as number | null,
    crmBusyMs: null as number | null,
    crmRequestsMeasured: 0,
  };
  const rows = logs
    .map(log => ({ ...log, at: log.timestamp == null ? NaN : new Date(log.timestamp).getTime() }))
    .filter(log => Number.isFinite(log.at))
    .sort((a, b) => a.at - b.at);
  let speechEnd: number | undefined;
  let gotTranscription = false;
  const requests = new Map<string, [number, number]>();
  const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0;
  for (const log of rows) {
    let payload: Record<string, any>;
    try {
      payload = JSON.parse(log.payload ?? '{}');
    } catch {
      continue;
    }
    if (!payload || typeof payload !== 'object') continue;
    if (log.type === 'vad-start' || log.type === 'vad-real-start' || log.type === 'answer-generation-cancel') {
      speechEnd = undefined;
      gotTranscription = false;
    }
    if (log.type === 'vad-end') {
      speechEnd = log.at;
      gotTranscription = false;
    }
    if (log.type === 'transcription' && speechEnd !== undefined && !gotTranscription) {
      result.sttAfterSpeechMs = log.at - speechEnd;
      gotTranscription = true;
    }
    if (log.type === 'answer-generation-ttft' && finite(payload.ttft)) result.llmFirstTokenMs = payload.ttft;
    if (log.type === 'tts-time-to-first-token' && finite(payload.ttft)) {
      result.ttsFirstChunkMs = payload.ttft;
      if (speechEnd !== undefined && gotTranscription) result.speechToFirstAudioMs = log.at - speechEnd;
      speechEnd = undefined;
      gotTranscription = false;
    }
    if (log.type === 'crm-request-end' && typeof payload.requestID === 'string' && finite(payload.elapsedMs)) {
      if (!requests.has(payload.requestID)) requests.set(payload.requestID, [log.at - payload.elapsedMs, log.at]);
    }
  }
  // Union of request intervals, not a sum that double-counts concurrent reads.
  const intervals = [...requests.values()].sort((a, b) => a[0] - b[0]);
  if (intervals.length) {
    let [start, end] = intervals[0],
      total = 0;
    for (const [nextStart, nextEnd] of intervals.slice(1)) {
      if (nextStart <= end) end = Math.max(end, nextEnd);
      else {
        total += end - start;
        start = nextStart;
        end = nextEnd;
      }
    }
    result.crmBusyMs = Math.round(total + end - start);
    result.crmRequestsMeasured = intervals.length;
  }
  return result;
}
